import { jwtDecode } from "jwt-decode";
import localStorageService from "../services/localStorageService";

/**
 * Check if a JWT token is expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if token is expired, false otherwise
 */
export const isTokenExpired = (token) => {
    if (!token) return true;

    try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000; // Convert to seconds
        return decoded.exp < currentTime;
    } catch (error) {
        console.error("Error decoding token:", error);
        return true;
    }
};

/**
 * Check if a JWT token will expire soon (within the next 5 minutes)
 * @param {string} token - The JWT token to check
 * @param {number} bufferMinutes - Minutes before expiry (default: 5)
 * @returns {boolean} - True if token will expire soon
 */
export const isTokenExpiringSoon = (token, bufferMinutes = 5) => {
    if (!token) return true;

    try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        const bufferTime = bufferMinutes * 60;
        return decoded.exp < currentTime + bufferTime;
    } catch (error) {
        console.error("Error decoding token:", error);
        return true;
    }
};

/**
 * Get the time remaining until token expires
 * @param {string} token - The JWT token to check
 * @returns {number} - Seconds until expiration, or 0 if expired
 */
export const getTokenExpiryTime = (token) => {
    if (!token) return 0;

    try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        const timeRemaining = decoded.exp - currentTime;
        return timeRemaining > 0 ? timeRemaining : 0;
    } catch (error) {
        console.error("Error decoding token:", error);
        return 0;
    }
};

/**
 * Check if access token needs refresh
 * @returns {Promise<boolean>} - True if token needs refresh
 */
export const shouldRefreshToken = async () => {
    const accessToken = await localStorageService.getItem("access_token");
    const refreshToken = await localStorageService.getItem("refresh_token");

    if (!refreshToken) return false;
    return isTokenExpiringSoon(accessToken);
};

/**
 * Check if refresh token is valid
 * @returns {Promise<boolean>} - True if refresh token is valid
 */
export const isRefreshTokenValid = async () => {
    const refreshToken = await localStorageService.getItem("refresh_token");
    return !isTokenExpired(refreshToken);
};
